import React, { useCallback, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { routerHelperActions } from '@wfp-common/store/routerHelperSlice';

import FeatureButton from '../utils/FeatureButton';
import State from '../app/store/state';
import { AuthState } from '../login/auth';
import {
    ActionCreators,
    defaultVendorUsersSortingOrder,
    Vendor,
    VendorUser,
    vendorUserColumnToAttributeMapping,
    VendorUserSortAttributes,
    VendorUserTableColumns,
} from './vendors';
import { SortableTableHead } from '../utils/table/table-head/sortable-table-head';
import { VendorUserRow } from './VendorUserRow';
import { ChangeSortOptions, SortOptions } from '../utils/hooks/useSort';
import { VendorUserFilters } from './VendorUserFilters';
import { ManagerPermission } from '../permission-profiles/permission';

interface Props {
    vendor: Vendor;
    auth: AuthState;
    vendorId: string;
    vendorUsersList: Array<VendorUser>;
    authorizeVendorUser: (vendorUserId: string) => void;
    requestPasswordReset: (vendorUser: VendorUser) => void;
    requestOtpReset: (vendorUser: VendorUser) => void;
    redirectToPage: (url: string) => void;
    cancelVendorUser: (vendorUserId: string) => void;
}

function VendorUserList({
    auth,
    vendorId,
    vendorUsersList,
    vendor,
    authorizeVendorUser,
    requestPasswordReset,
    requestOtpReset,
    redirectToPage,
    cancelVendorUser,
}: Props) {
    const [sortOptions, setSortOptions] =
        useState<SortOptions<VendorUserSortAttributes>>(defaultVendorUsersSortingOrder);

    const handleVendorUserChosen = useCallback(
        (id) => () => {
            redirectToPage(`/vendors/${vendorId}/panel/${id}`);
        },
        [vendorId]
    );
    const handleSort: ChangeSortOptions<VendorUserTableColumns> = useCallback(
        ([column, direction]) => {
            if (column) {
                setSortOptions(
                    new SortOptions<VendorUserSortAttributes>({
                        column: vendorUserColumnToAttributeMapping[column],
                        direction,
                    })
                );
            } else {
                setSortOptions(defaultVendorUsersSortingOrder);
            }
        },
        [vendorId]
    );
    return (
        <div>
            {vendor?.authorizedAt && (
                <FeatureButton
                    linkTo={`/vendors/${vendorId}/panel/add-user`}
                    manager={auth.manager}
                    restrictedToFeatures={[ManagerPermission.vendorUserPark]}
                    title={'Add Vendor User'}
                />
            )}
            <VendorUserFilters sortOptions={sortOptions} vendorId={vendorId} />
            <table className="wfp-table mt4 table-hover">
                <SortableTableHead changeSortOptions={handleSort}>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Expiration Date</th>
                    <SortableTableHead.DisabledColumn />
                </SortableTableHead>
                <tbody>
                    {vendorUsersList?.map((vendorUser) => (
                        <VendorUserRow
                            auth={auth}
                            authorizeVendorUser={authorizeVendorUser}
                            cancelVendorUser={cancelVendorUser}
                            key={vendorUser.id}
                            onVendorUserChosen={handleVendorUserChosen(vendorUser.id)}
                            requestOtpReset={requestOtpReset}
                            requestPasswordReset={requestPasswordReset}
                            vendorUser={vendorUser}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function mapStateToProps(state: State) {
    return {
        auth: state.auth,
        vendorUsersList: state.vendors.usersList,
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return {
        authorizeVendorUser: bindActionCreators(ActionCreators.authorizeVendorUser, dispatch),
        cancelVendorUser: bindActionCreators(ActionCreators.cancelVendorUserPark, dispatch),
        requestPasswordReset: bindActionCreators(ActionCreators.requestPasswordReset, dispatch),
        requestOtpReset: bindActionCreators(ActionCreators.requestOtpReset, dispatch),
        redirectToPage: (url) => dispatch(routerHelperActions.makeRedirect(url)),
    };
}

const withHooks = (Component: any) => {
    const WithHooks = (props: any) => {
        const params = useParams();
        const vendorId = params.vendorId;
        const { vendors } = useSelector((state: State) => state);
        const vendor = vendors.list.find((v) => v.id.toString() === vendorId) || new Vendor();

        return <Component {...props} vendor={vendor} vendorId={vendorId} />;
    };

    WithHooks.displayName = `withHooks(${Component.displayName || Component.name || 'Component'})`;

    return WithHooks;
};

export default connect(mapStateToProps, mapDispatchToProps)(withHooks(VendorUserList));
